import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Icon, Text } from '@chakra-ui/react';
import { FiChevronRight } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { fetchGoalsRollup } from '../../api/endpointsBackend';
import { useDisplayCurrency } from '../../contexts/DisplayCurrencyContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import { useGoalsView } from '../../hooks/useGoalsView';
import { monthLong } from '../../utils/intlFormatters';
import { daysLeftInMonth, formatAmount } from '../../utils/goals';
import GoalMonthRing from '../goals/GoalMonthRing';
import GoalIcon from '../goals/GoalIcon';
import { GoalBar } from '../goals/GoalProgress';
import Figure from '../shared/Figure';
import { cardStyle, cardTitle, viewAllStyle } from './dashboardStyles';

const TOP_N = 3;

/**
 * Goals at a glance: this month's saving across active goals, and the three
 * active goals with their progress. Each row opens that goal.
 */
export default function DashboardGoalsCard() {
  const { t } = useTranslation('goals');
  const navigate = useNavigate();
  const { displayCurrency } = useDisplayCurrency();
  const { homeCurrency } = usePreferences();
  const { rows } = useGoalsView({ includeArchived: false });
  const [rollup, setRollup] = useState(null);

  const active = rows.filter((g) => g.status === 'active');
  const daysLeft = daysLeftInMonth();
  const currency = displayCurrency || homeCurrency || active[0]?.currency;

  useEffect(() => {
    if (!currency) return undefined;
    let isCurrent = true;
    fetchGoalsRollup(currency)
      .then((data) => { if (isCurrent) setRollup(data); })
      .catch(() => { if (isCurrent) setRollup(null); });
    return () => { isCurrent = false; };
  }, [currency]);

  return (
    <Box {...cardStyle}>
      <Flex justify="space-between" align="center" mb={4} gap={2}>
        <Text {...cardTitle}>{t('card.title')}</Text>
        <Text {...viewAllStyle} onClick={() => navigate('/goals')} flexShrink={0}>
          {t('card.viewAll')} <Icon as={FiChevronRight} w="14px" h="14px" />
        </Text>
      </Flex>

      {active.length === 0 ? (
        <Text fontSize="sm" color="redesign.text.muted">{t('card.empty')}</Text>
      ) : (
        <>
          {rollup && (
            <>
              <Flex align="center" gap={4} mb={3}>
                {rollup.required > 0 && (
                  <GoalMonthRing pct={(rollup.saved / rollup.required) * 100} size={56} stroke={6} />
                )}
                <Box minW={0}>
                  <Figure size="md" code={rollup.currency} amount={formatAmount(rollup.saved)} display="block" />
                  <Text fontSize="xs" color="redesign.text.muted" mt={1}>
                    {t('rollup.savedIn', { month: monthLong(new Date().getMonth()) })}
                  </Text>
                  {/* What the month still asks for, and how long is left to do it. */}
                  <Text fontSize="xs" color="redesign.text.muted" mt={1}>
                    <Text as="span" color="redesign.text">{formatAmount(rollup.to_go)}</Text>
                    {' '}{t('rollup.toGoInline')}
                    {' · '}
                    {t(daysLeft === 1 ? 'rollup.daysLeftOne' : 'rollup.daysLeftOther', { count: daysLeft })}
                  </Text>
                </Box>
              </Flex>
              <Box h="1px" bg="redesign.border" mb={3} />
            </>
          )}
          <Flex direction="column" gap={2}>
            {active.slice(0, TOP_N).map((goal) => (
              <Flex
                key={goal.id}
                align="center"
                gap={3}
                cursor="pointer"
                borderRadius="md"
                mx={-1}
                px={1}
                py="2px"
                _hover={{ bg: 'redesign.bg.hover' }}
                onClick={() => navigate(`/goals?goal_id=${goal.id}`)}
              >
                <GoalIcon goal={goal} size={22} />
                <Text fontSize="sm" color="redesign.text" maxW="45%" isTruncated>{goal.name}</Text>
                <Box flex={1} minW="60px">
                  <GoalBar pct={goal.summary?.pct} color={goal.color} />
                </Box>
                <Text fontSize="xs" fontWeight={600} color="redesign.text.muted" w="38px" textAlign="right" flexShrink={0}>
                  {goal.summary ? `${Math.round(goal.summary.pct)}%` : '—'}
                </Text>
              </Flex>
            ))}
          </Flex>
        </>
      )}
    </Box>
  );
}
